import { useCallback, useEffect, useRef, useState } from 'react';
import {
  searchPaczkomats,
  getPaczkomat,
  getGeowidgetToken,
  type SelectedPaczkomat,
} from '../api/paczkomats';
import { ApiError } from '../api/client';

type Theme = 'shop' | 'admin';

type Props = {
  value: SelectedPaczkomat | null;
  onChange: (next: SelectedPaczkomat | null) => void;
  disabled?: boolean;
  theme?: Theme;
};

const GEOWIDGET_SCRIPT_SRC = 'https://geowidget.inpost.pl/inpost-geowidget.js';
const GEOWIDGET_STYLE_HREF = 'https://geowidget.inpost.pl/inpost-geowidget.css';
const SEARCH_MIN_CHARS = 3;
const SEARCH_DEBOUNCE_MS = 200;
// Geowidget v5 takes the handler name from the `onpoint` attribute and dispatches
// the selection as a bubbling CustomEvent of that name — there is no literal
// `onpoint` event, and without the attribute the widget emits nothing at all.
const GEOWIDGET_POINT_EVENT = 'onpointselect';

let geowidgetPromise: Promise<void> | null = null;

function loadGeowidget(): Promise<void> {
  if (geowidgetPromise) return geowidgetPromise;
  geowidgetPromise = new Promise<void>((resolve, reject) => {
    if (customElements.get('inpost-geowidget')) {
      resolve();
      return;
    }
    if (!document.querySelector(`link[href="${GEOWIDGET_STYLE_HREF}"]`)) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = GEOWIDGET_STYLE_HREF;
      document.head.appendChild(link);
    }
    if (document.querySelector(`script[src="${GEOWIDGET_SCRIPT_SRC}"]`)) {
      // Another mount kicked it off; wait for definition.
      customElements
        .whenDefined('inpost-geowidget')
        .then(() => resolve())
        .catch(reject);
      return;
    }
    const script = document.createElement('script');
    script.src = GEOWIDGET_SCRIPT_SRC;
    script.async = true;
    script.onload = () => {
      customElements
        .whenDefined('inpost-geowidget')
        .then(() => resolve())
        .catch(reject);
    };
    script.onerror = () => {
      geowidgetPromise = null;
      reject(new Error('geowidget script failed to load'));
    };
    document.head.appendChild(script);
  });
  return geowidgetPromise;
}

type GeowidgetPoint = {
  name?: string;
  address_details?: { street?: string; post_code?: string; city?: string };
  location?: { latitude?: number; longitude?: number };
  status?: string;
  type?: string[];
};

// The widget only lists collectable lockers under config="parcelCollect", and the
// server re-validates the code before the order is created, so anything the payload
// omits is checked elsewhere — rejecting on a missing field would silently swallow
// the shopper's click.
function pointToSelection(detail: GeowidgetPoint | null | undefined): SelectedPaczkomat | null {
  if (!detail?.name) return null;
  if (detail.status && detail.status !== 'Operating') return null;
  if (detail.type && !detail.type.includes('parcel_locker')) return null;
  const street = detail.address_details?.street ?? '';
  return {
    code: detail.name,
    name: street || detail.name,
    street,
    postCode: detail.address_details?.post_code ?? '',
    city: detail.address_details?.city ?? '',
    latitude: detail.location?.latitude ?? 0,
    longitude: detail.location?.longitude ?? 0,
  };
}

export function PaczkomatPicker({ value, onChange, disabled, theme = 'shop' }: Props) {
  const [query, setQuery] = useState('');
  const [items, setItems] = useState<SelectedPaczkomat[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [highlight, setHighlight] = useState(0);
  const [showList, setShowList] = useState(false);
  const [mapOpen, setMapOpen] = useState(false);
  const [mapError, setMapError] = useState<string | null>(null);
  const [mapReady, setMapReady] = useState(false);
  const [geowidgetToken, setGeowidgetToken] = useState<string | null>(null);

  const inputRef = useRef<HTMLInputElement | null>(null);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const debounceRef = useRef<number | null>(null);
  const validatedOnceRef = useRef<string | null>(null);

  const c = theme === 'admin' ? ADMIN_TOKENS : SHOP_TOKENS;

  // Re-validate a pre-existing selection when the picker mounts (e.g. user
  // navigated back to checkout) — a locker may have gone offline since.
  useEffect(() => {
    if (!value) return;
    if (validatedOnceRef.current === value.code) return;
    validatedOnceRef.current = value.code;
    void getPaczkomat(value.code).catch((err: unknown) => {
      if (err instanceof ApiError && err.status === 404) {
        setError('Wybrany paczkomat nie jest już dostępny - wybierz inny.');
        onChange(null);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Close suggestion list on outside click.
  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (!wrapperRef.current) return;
      if (!wrapperRef.current.contains(e.target as Node)) {
        setShowList(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const runSearch = useCallback(async (q: string) => {
    abortRef.current?.abort();
    const ctl = new AbortController();
    abortRef.current = ctl;
    setLoading(true);
    setError(null);
    try {
      const { items } = await searchPaczkomats(q, ctl.signal);
      setItems(items);
      setHighlight(0);
    } catch (err) {
      if ((err as { name?: string }).name === 'AbortError') return;
      const status = (err as { status?: number }).status;
      if (status === 429) {
        setError('Za szybko wpisujesz - chwila…');
      } else if (status === 400) {
        // query_too_short — silently keep list empty
        setItems([]);
      } else {
        setError('Nie udało się pobrać paczkomatów.');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const onQueryChange = (next: string) => {
    setQuery(next);
    setShowList(true);
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    if (next.trim().length < SEARCH_MIN_CHARS) {
      setItems([]);
      return;
    }
    debounceRef.current = window.setTimeout(() => {
      void runSearch(next.trim());
    }, SEARCH_DEBOUNCE_MS);
  };

  const pick = (p: SelectedPaczkomat) => {
    onChange(p);
    setQuery('');
    setItems([]);
    setShowList(false);
    setError(null);
  };

  const onKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showList || items.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, items.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const picked = items[highlight];
      if (picked) pick(picked);
    } else if (e.key === 'Escape') {
      setShowList(false);
    }
  };

  const openMap = async () => {
    setMapError(null);
    setMapOpen(true);
    setMapReady(false);
    try {
      const [{ token }] = await Promise.all([getGeowidgetToken(), loadGeowidget()]);
      setGeowidgetToken(token);
      setMapReady(true);
    } catch (err) {
      console.error('[paczkomat] geowidget load failed', err);
      setMapError('Mapa chwilowo niedostępna - użyj wyszukiwarki powyżej.');
    }
  };

  const closeMap = () => {
    setMapOpen(false);
    setMapReady(false);
    setMapError(null);
  };

  // Wire the Geowidget's point selection while the map is open.
  useEffect(() => {
    if (!mapOpen || !mapReady) return;
    const onPoint = (e: Event) => {
      const selection = pointToSelection((e as CustomEvent<GeowidgetPoint>).detail);
      if (!selection) return;
      onChange(selection);
      closeMap();
    };
    document.addEventListener(GEOWIDGET_POINT_EVENT, onPoint);
    return () => document.removeEventListener(GEOWIDGET_POINT_EVENT, onPoint);
  }, [mapOpen, mapReady, onChange]);

  if (value) {
    return (
      <div style={c.selectedWrap}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={c.selectedCode}>{value.code}</div>
          <div style={c.selectedAddr}>
            {value.street}, {value.postCode} {value.city}
          </div>
        </div>
        <button
          type="button"
          onClick={() => {
            onChange(null);
            validatedOnceRef.current = null;
            setTimeout(() => inputRef.current?.focus(), 0);
          }}
          style={c.changeBtn}
          disabled={disabled}
        >
          ZMIEŃ
        </button>
      </div>
    );
  }

  return (
    <div ref={wrapperRef} style={{ position: 'relative' }}>
      <div style={c.inputRow}>
        <input
          ref={inputRef}
          className="input"
          style={c.input}
          value={query}
          onChange={(e) => onQueryChange(e.target.value)}
          onFocus={() => setShowList(true)}
          onKeyDown={onKey}
          placeholder="Szukaj paczkomatu - kod, ulica lub miasto"
          disabled={disabled}
          autoComplete="off"
        />
        <button
          type="button"
          onClick={() => void openMap()}
          style={c.mapBtn}
          disabled={disabled}
        >
          OTWÓRZ MAPĘ
        </button>
      </div>

      {error && <div style={c.error}>{error}</div>}

      {showList && (query.trim().length >= SEARCH_MIN_CHARS || items.length > 0) && (
        <div style={c.list}>
          {loading && <div style={c.listEmpty}>Wyszukiwanie…</div>}
          {!loading && items.length === 0 && (
            <div style={c.listEmpty}>Brak wyników - spróbuj nazwy miasta lub kodu.</div>
          )}
          {!loading &&
            items.map((it, i) => (
              <button
                key={it.code}
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault();
                  pick(it);
                }}
                onMouseEnter={() => setHighlight(i)}
                style={{
                  ...c.listItem,
                  ...(i === highlight ? c.listItemActive : null),
                }}
              >
                <div style={c.listItemCode}>{it.code}</div>
                <div style={c.listItemAddr}>
                  {it.street}, {it.postCode} {it.city}
                </div>
              </button>
            ))}
        </div>
      )}

      {mapOpen && (
        <div style={modalBackdrop} onClick={closeMap}>
          <div style={modalBody} onClick={(e) => e.stopPropagation()}>
            <div style={modalHeader}>
              <span style={modalTitle}>WYBIERZ PACZKOMAT</span>
              <button type="button" onClick={closeMap} style={modalCloseBtn}>
                ZAMKNIJ ×
              </button>
            </div>
            <div style={modalContent}>
              {mapError && <div style={mapErrorStyle}>{mapError}</div>}
              {!mapError && !mapReady && (
                <div style={mapLoading}>Wczytywanie mapy…</div>
              )}
              {!mapError && mapReady && geowidgetToken && (
                <inpost-geowidget
                  token={geowidgetToken}
                  language="pl"
                  config="parcelCollect"
                  onpoint={GEOWIDGET_POINT_EVENT}
                  style={{ width: '100%', height: '100%', display: 'block' }}
                />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

declare global {
  // eslint-disable-next-line @typescript-eslint/no-namespace
  namespace JSX {
    interface IntrinsicElements {
      'inpost-geowidget': React.DetailedHTMLProps<
        React.HTMLAttributes<HTMLElement> & {
          token?: string;
          language?: string;
          config?: string;
          onpoint?: string;
        },
        HTMLElement
      >;
    }
  }
}

// ----------------------------- styles -----------------------------

const SHOP_TOKENS = {
  selectedWrap: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: 12,
    border: '2px solid var(--orange)',
    background: 'rgba(255,107,26,.08)',
  } as React.CSSProperties,
  selectedCode: {
    fontFamily: 'var(--display)',
    fontSize: 16,
    letterSpacing: '.06em',
    color: 'var(--orange)',
  } as React.CSSProperties,
  selectedAddr: {
    fontFamily: 'var(--mono)',
    fontSize: 12,
    color: 'var(--shop-fg)',
    marginTop: 2,
  } as React.CSSProperties,
  changeBtn: {
    padding: '6px 10px',
    background: 'transparent',
    border: '1px solid var(--orange)',
    color: 'var(--orange)',
    fontFamily: 'var(--display)',
    fontSize: 11,
    letterSpacing: '.1em',
    cursor: 'pointer',
  } as React.CSSProperties,
  inputRow: { display: 'flex', gap: 8 } as React.CSSProperties,
  input: { flex: 1, border: '2px solid var(--shop-fg)' } as React.CSSProperties,
  mapBtn: {
    padding: '0 14px',
    background: 'var(--orange)',
    color: '#fff',
    border: '2px solid var(--orange)',
    fontFamily: 'var(--display)',
    fontSize: 12,
    letterSpacing: '.08em',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  } as React.CSSProperties,
  error: {
    marginTop: 8,
    padding: '8px 10px',
    border: '1px dashed var(--orange)',
    color: 'var(--orange)',
    fontFamily: 'var(--mono)',
    fontSize: 12,
  } as React.CSSProperties,
  list: {
    position: 'absolute',
    top: 'calc(100% + 4px)',
    left: 0,
    right: 0,
    background: 'var(--shop-bg)',
    border: '2px solid var(--shop-fg)',
    zIndex: 50,
    maxHeight: 320,
    overflowY: 'auto',
  } as React.CSSProperties,
  listEmpty: {
    padding: 12,
    fontSize: 12,
    color: 'rgba(var(--shop-muted-rgb),.6)',
  } as React.CSSProperties,
  listItem: {
    display: 'block',
    width: '100%',
    textAlign: 'left',
    padding: '10px 12px',
    background: 'transparent',
    border: 0,
    borderTop: '1px solid rgba(var(--shop-muted-rgb),.15)',
    color: 'var(--shop-fg)',
    cursor: 'pointer',
  } as React.CSSProperties,
  listItemActive: {
    background: 'rgba(255,107,26,.12)',
  } as React.CSSProperties,
  listItemCode: {
    fontFamily: 'var(--display)',
    fontSize: 14,
    color: 'var(--orange)',
  } as React.CSSProperties,
  listItemAddr: {
    fontFamily: 'var(--mono)',
    fontSize: 11,
    color: 'var(--shop-fg)',
    marginTop: 2,
  } as React.CSSProperties,
};

const ADMIN_TOKENS = {
  ...SHOP_TOKENS,
  selectedWrap: {
    ...SHOP_TOKENS.selectedWrap,
    border: '1px solid var(--admin-line)',
    background: 'var(--admin-card-bg)',
  } as React.CSSProperties,
  selectedCode: {
    ...SHOP_TOKENS.selectedCode,
    color: 'var(--admin-fg)',
  } as React.CSSProperties,
  selectedAddr: {
    ...SHOP_TOKENS.selectedAddr,
    color: 'var(--admin-muted)',
  } as React.CSSProperties,
  changeBtn: {
    ...SHOP_TOKENS.changeBtn,
    border: '1px solid var(--admin-line)',
    color: 'var(--admin-fg)',
  } as React.CSSProperties,
  input: {
    ...SHOP_TOKENS.input,
    border: '1px solid var(--admin-line)',
  } as React.CSSProperties,
  mapBtn: {
    ...SHOP_TOKENS.mapBtn,
    background: 'var(--admin-fg)',
    color: 'var(--admin-bg)',
    border: '1px solid var(--admin-line)',
  } as React.CSSProperties,
};

const modalBackdrop: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,.6)',
  zIndex: 1000,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 12,
};
const modalBody: React.CSSProperties = {
  background: '#fff',
  width: 'min(960px, 100%)',
  height: 'min(720px, 90vh)',
  display: 'flex',
  flexDirection: 'column',
  border: '2px solid #000',
};
const modalHeader: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '10px 14px',
  borderBottom: '2px solid #000',
};
const modalTitle: React.CSSProperties = {
  fontFamily: 'var(--display)',
  fontSize: 14,
  letterSpacing: '.12em',
};
const modalCloseBtn: React.CSSProperties = {
  background: 'transparent',
  border: '1px solid #000',
  padding: '6px 10px',
  fontFamily: 'var(--display)',
  fontSize: 11,
  letterSpacing: '.1em',
  cursor: 'pointer',
};
const modalContent: React.CSSProperties = {
  flex: 1,
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};
const mapLoading: React.CSSProperties = {
  fontFamily: 'var(--mono)',
  fontSize: 13,
  color: '#444',
};
const mapErrorStyle: React.CSSProperties = {
  padding: 20,
  textAlign: 'center',
  fontFamily: 'var(--mono)',
  fontSize: 13,
  color: '#b00',
};
